import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Star, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { useSubscription } from '@/contexts/useSubscription';
import { createPortal } from '@/lib/api/subscription';
import { getSubscriptionError } from '@/lib/errors';
import { captureFriendlyError } from '@/lib/sentry';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import type { ApiError } from '@/lib/api/client';
import { AUTH_ENABLED } from '@/lib/prelaunch';
import PageContainer from '@/components/layout/PageContainer';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import EditableNameRow from '@/components/settings/EditableNameRow';
import EditableInterviewDateRow from '@/components/settings/EditableInterviewDateRow';
import EditableEnglishLevelRow from '@/components/settings/EditableEnglishLevelRow';
import EditableConcernsRow from '@/components/settings/EditableConcernsRow';
import NotificationPreferencesCard from '@/components/settings/NotificationPreferencesCard';
import HelpAndLegalCard from '@/components/settings/HelpAndLegalCard';
import DeleteAccountCard from '@/components/settings/DeleteAccountCard';

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay },
});

function formatPeriodEnd(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { isPremium, cadence, currentPeriodEnd, cancelAtPeriodEnd, cancelAt } =
    useSubscription();

  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<FriendlyError | null>(null);

  const maxAttempts = 5;
  const attemptsRemaining = maxAttempts - (profile?.free_attempts_used ?? 0);

  const handleSignOut = async () => {
    const confirmed = window.confirm('Are you sure you want to sign out?');
    if (!confirmed) return;
    await signOut();
    navigate(AUTH_ENABLED ? '/auth' : '/', { replace: true });
  };

  const handleManage = async () => {
    setPortalLoading(true);
    setPortalError(null);
    track('billing_portal_opened');
    try {
      const { portal_url } = await createPortal(
        `${window.location.origin}/settings`
      );
      window.location.href = portal_url;
    } catch (err) {
      const apiError = err as ApiError;
      const friendly = getSubscriptionError(apiError.code || 'portal_failed');
      captureFriendlyError(err, friendly);
      setPortalError(friendly);
      setPortalLoading(false);
    }
  };

  const renewLabel = formatPeriodEnd(currentPeriodEnd);
  const cancelAtLabel = formatPeriodEnd(cancelAt);

  let subscriptionDetail: string;
  if (isPremium) {
    if (cancelAt && cancelAtLabel) {
      subscriptionDetail = `Ends ${cancelAtLabel}`;
    } else if (cancelAtPeriodEnd && renewLabel) {
      subscriptionDetail = `Ends ${renewLabel}`;
    } else if (renewLabel) {
      subscriptionDetail = `Renews ${renewLabel}`;
    } else {
      subscriptionDetail = 'Active';
    }
  } else {
    subscriptionDetail =
      attemptsRemaining > 0
        ? `${attemptsRemaining} free attempt${attemptsRemaining !== 1 ? 's' : ''} remaining`
        : 'No free attempts remaining';
  }

  return (
    <div className="min-h-screen bg-cream">
      <PageContainer size="default">
        <motion.div {...fadeInUp(0)} className="mb-8 md:mb-12">
          <p className="font-sans text-sm text-text-muted uppercase tracking-widest mb-2 hidden md:block">
            Account
          </p>
          <h1 className="font-display font-semibold text-3xl md:text-5xl text-text-primary">
            Settings
          </h1>
        </motion.div>

        <motion.div {...fadeInUp(0.05)} className="mb-4 md:mb-6">
          <EditableNameRow />
        </motion.div>

        <motion.div {...fadeInUp(0.1)} className="mb-4 md:mb-6">
          <EditableInterviewDateRow />
        </motion.div>

        <motion.div {...fadeInUp(0.15)} className="mb-4 md:mb-6">
          <EditableEnglishLevelRow />
        </motion.div>

        <motion.div {...fadeInUp(0.2)} className="mb-4 md:mb-6">
          <EditableConcernsRow />
        </motion.div>

        <motion.div {...fadeInUp(0.25)} className="mb-4 md:mb-6">
          <Card>
            <div className="flex items-center gap-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isPremium ? 'bg-primary-light' : 'bg-warning-light'
                }`}
              >
                {isPremium ? (
                  <Sparkles size={20} className="text-primary" />
                ) : (
                  <Star size={20} className="text-warning" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-sans text-sm text-text-muted">Subscription</p>
                <p className="font-sans font-semibold text-base text-text-primary">
                  {isPremium
                    ? cadence === 'annual'
                      ? 'Premium · Annual'
                      : 'Premium · Monthly'
                    : 'Free Plan'}
                </p>
                <p className="font-sans text-xs text-text-secondary mt-0.5">
                  {subscriptionDetail}
                </p>
              </div>
            </div>

            {portalError && (
              <div className="mt-4 rounded-2xl bg-error-light border border-error/20 px-4 py-3">
                <p className="font-display font-semibold text-base text-error leading-tight">
                  {portalError.title}
                </p>
                <p className="font-sans text-sm text-error/80 mt-1">
                  {portalError.message}
                </p>
              </div>
            )}

            <div className="mt-4">
              {isPremium ? (
                <Button fullWidth onClick={handleManage} loading={portalLoading}>
                  Manage Subscription
                </Button>
              ) : (
                <Button
                  fullWidth
                  onClick={() =>
                    navigate('/pricing', { state: { from: '/settings' } })
                  }
                >
                  Upgrade to Premium
                </Button>
              )}
            </div>
          </Card>
        </motion.div>

        <motion.div {...fadeInUp(0.3)} className="mb-4 md:mb-6">
          <NotificationPreferencesCard />
        </motion.div>

        <motion.div {...fadeInUp(0.35)} className="mb-4 md:mb-6">
          <HelpAndLegalCard />
        </motion.div>

        <motion.div {...fadeInUp(0.4)} className="mb-4 md:mb-6">
          <Card>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full cursor-pointer"
            >
              <LogOut size={20} className="text-error" />
              <span className="font-sans font-medium text-base text-error">
                Sign Out
              </span>
            </button>
          </Card>
        </motion.div>

        <motion.div {...fadeInUp(0.45)} className="mb-8">
          <DeleteAccountCard />
        </motion.div>

        <motion.p
          {...fadeInUp(0.5)}
          className="text-center font-sans text-xs text-text-muted"
        >
          Version 1.0.0
        </motion.p>
      </PageContainer>
    </div>
  );
}
